import { useState, useCallback } from "react";
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import i18n from "@/i18n";

/**
 * Convertit un Blob PDF en base64 (pour sauvegarde ou partage sur mobile)
 */
export async function blobToBase64(blob: Blob): Promise<string> {
  const arrayBuffer = await blob.arrayBuffer();
  return btoa(
    new Uint8Array(arrayBuffer)
      .reduce((data, byte) => data + String.fromCharCode(byte), "")
  );
}

/**
 * Télécharge/partage un PDF à partir d'un blob (multi-plateforme)
 */
export async function downloadAndOpenPDF(blob: Blob, fileName: string) {
  const base64 = await blobToBase64(blob);

  if (Capacitor.isNativePlatform()) {
    // Sur mobile : sauvegarde et propose de partager
    await Filesystem.writeFile({
      path: fileName,
      data: base64,
      directory: Directory.Documents,
    });

    
    await Share.share({
      title: i18n.t('mobileDownload.invoiceTitle', { ns: 'appui', fileName }),
      text: i18n.t('mobileDownload.invoiceReady', { ns: 'appui' }),
      url: `data:application/pdf;base64,${base64}`,
      dialogTitle: i18n.t('mobileDownload.shareInvoice', { ns: 'appui' })
    });
  } else {
    // Sur web : téléchargement classique
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      URL.revokeObjectURL(link.href);
      link.remove();
    }, 500);
  }
}

/**
 * Télécharge un PDF depuis une URL, puis le sauvegarde/partage (multi-plateforme)
 */
export async function downloadAndOpenPdfFromUrl(pdfUrl: string, fileName: string) {
  const response = await fetch(pdfUrl);
  const blob = await response.blob();
  await downloadAndOpenPDF(blob, fileName);
}

/**
 * Hook React prêt à l'emploi pour gérer le téléchargement multi-plateforme
 */
export function useDownloadPDF() {
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Télécharge un blob PDF
  const download = useCallback(async (blob: Blob, fileName: string) => {
    setIsDownloading(true);
    setError(null);
    try {
      await downloadAndOpenPDF(blob, fileName);
    } catch (err: any) {
      setError(err);
      throw err;
    } finally {
      setIsDownloading(false);
    }
  }, []);

  // Télécharge un PDF depuis une URL
  const downloadFromUrl = useCallback(async (pdfUrl: string, fileName: string) => {
    setIsDownloading(true);
    setError(null);
    try {
      await downloadAndOpenPdfFromUrl(pdfUrl, fileName);
    } catch (err: any) {
      setError(err);
      throw err;
    } finally {
      setIsDownloading(false);
    }
  }, []);

  return {
    isDownloading,
    error,
    download,        // (blob, fileName) => void
    downloadFromUrl, // (pdfUrl, fileName) => void
  };
}
